import type { SpellKind } from '../game/content';

// Procedural Web Audio: every sound is synthesised, so there are no asset files to load.
export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private last = new Map<string, number>();
  private sfxVolume = 0.7;
  private musicVolume = 0.45;
  private musicTimer = 0;
  private nextNote = 0;
  private step = 0;
  private music: { root: number; mode: number[]; tempo: number } | null = null;
  private intensity = 0;

  private readonly unlock = () => {
    void this.init();
  };

  constructor() {
    window.addEventListener('pointerdown', this.unlock);
    window.addEventListener('keydown', this.unlock);
  }

  private async init(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state !== 'running') await this.ctx.resume(); // iOS also leaves it 'interrupted' after a call or lock
      return;
    }
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 4;
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.sfxBus = this.ctx.createGain();
    this.musicBus = this.ctx.createGain();
    this.sfxBus.gain.value = this.sfxVolume;
    this.musicBus.gain.value = this.musicVolume;
    this.sfxBus.connect(this.master);
    this.musicBus.connect(this.master);
    this.master.connect(comp).connect(this.ctx.destination);
    const len = this.ctx.sampleRate;
    this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1; // audio texture only, not gameplay
    // The unlock listeners stay: the browser can suspend the context again later, and only a gesture resumes it.
    await this.ctx.resume();
  }

  setVolumes(sfx: number, music: number): void {
    this.sfxVolume = sfx;
    this.musicVolume = music;
    if (this.sfxBus) this.sfxBus.gain.value = sfx;
    if (this.musicBus) this.musicBus.gain.value = music;
  }

  // --- music: a small generative sequencer (pad + bass + arpeggio + pulse) ---

  playMusic(music: { root: number; mode: number[]; tempo: number } | null): void {
    this.music = music;
    this.step = 0;
    this.nextNote = this.ctx ? this.ctx.currentTime + 0.1 : 0;
  }

  setIntensity(value: number): void {
    this.intensity = value;
  }

  update(dt: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.music || !this.musicBus || ctx.state !== 'running') return;
    this.musicTimer -= dt;
    if (this.musicTimer > 0) return;
    this.musicTimer = 0.05;
    const beat = 60 / this.music.tempo / 2; // eighth notes
    if (this.nextNote < ctx.currentTime) this.nextNote = ctx.currentTime + 0.05;
    while (this.nextNote < ctx.currentTime + 0.2) {
      this.scheduleStep(this.step, this.nextNote, beat);
      this.nextNote += beat;
      this.step += 1;
    }
  }

  private scheduleStep(step: number, t: number, beat: number): void {
    const m = this.music!;
    const bar = Math.floor(step / 16);
    const prog = [0, 5, 3, 4][bar % 4];
    const degree = (d: number) => {
      const oct = Math.floor(d / m.mode.length);
      return m.mode[((d % m.mode.length) + m.mode.length) % m.mode.length] + oct * 12;
    };
    const freq = (semi: number) => m.root * Math.pow(2, semi / 12);
    const bus = this.musicBus!;
    if (step % 16 === 0) {
      for (const d of [0, 2, 4]) this.tone(freq(degree(prog + d) - 12), t, beat * 16, 'sawtooth', 0.018, bus, 900);
    }
    if (step % 4 === 0) this.tone(freq(degree(prog) - 24), t, beat * 3.5, 'triangle', 0.09, bus, 400);
    const arp = [0, 2, 4, 7, 4, 2, 4, 9];
    if (step % 2 === 0 || this.intensity > 0.6) {
      this.tone(freq(degree(prog + arp[step % 8])), t, beat * 0.9, 'triangle', 0.028 + this.intensity * 0.012, bus, 2400);
    }
    if (this.intensity > 0.3 && step % 4 === 0) this.thump(t, 0.12 + this.intensity * 0.1, bus);
    if (this.intensity > 0.75 && step % 4 === 2) this.hat(t, 0.04, bus);
  }

  // --- sfx ---

  private ok(key: string, minGap: number): boolean {
    if (!this.ctx || this.ctx.state !== 'running') return false;
    const now = this.ctx.currentTime;
    if (now - (this.last.get(key) ?? -1) < minGap) return false;
    this.last.set(key, now);
    return true;
  }

  cast(kind: SpellKind | 'orbit'): void {
    if (!this.ok(`cast-${kind}`, 0.06)) return;
    const t = this.ctx!.currentTime;
    const bus = this.sfxBus!;
    switch (kind) {
      case 'missile':
        this.sweep(880, 1600, t, 0.12, 'sine', 0.05, bus);
        break;
      case 'bomb':
        this.sweep(300, 120, t, 0.25, 'sawtooth', 0.04, bus);
        this.noiseBurst(t, 0.2, 0.05, 1200, bus);
        break;
      case 'nova':
        this.sweep(1400, 300, t, 0.35, 'sine', 0.07, bus);
        this.noiseBurst(t, 0.3, 0.05, 4000, bus);
        break;
      case 'chain':
        this.noiseBurst(t, 0.15, 0.09, 6000, bus);
        this.sweep(2200, 600, t, 0.12, 'square', 0.03, bus);
        break;
      case 'orbit':
        this.sweep(500, 900, t, 0.3, 'sine', 0.05, bus);
        break;
      case 'zone':
        this.sweep(600, 200, t, 0.2, 'triangle', 0.05, bus);
        break;
      case 'beam':
        this.sweep(300, 1200, t, 0.25, 'sawtooth', 0.04, bus);
        break;
      case 'boomerang':
        this.noiseBurst(t, 0.25, 0.05, 2500, bus);
        break;
      case 'strike':
        this.sweep(160, 50, t, 0.25, 'square', 0.06, bus);
        this.noiseBurst(t, 0.18, 0.07, 700, bus);
        break;
      case 'volley':
        this.noiseBurst(t, 0.06, 0.04, 5000, bus);
        break;
      case 'vortex':
        this.sweep(120, 60, t, 0.8, 'sawtooth', 0.05, bus);
        break;
      case 'aura':
        break;
      case 'burst':
        this.noiseBurst(t, 0.08, 0.04, 7000, bus);
        this.sweep(1800, 2600, t, 0.1, 'sine', 0.03, bus);
        break;
      case 'drain':
        this.sweep(700, 260, t, 0.3, 'sine', 0.05, bus);
        break;
      case 'rune':
        this.sweep(400, 800, t, 0.18, 'triangle', 0.04, bus);
        break;
      case 'wave':
        this.noiseBurst(t, 0.5, 0.06, 900, bus, 0.12);
        break;
    }
  }

  hit(crit: boolean): void {
    if (!this.ok('hit', 0.025)) return;
    const t = this.ctx!.currentTime;
    this.noiseBurst(t, 0.05, crit ? 0.06 : 0.035, crit ? 3500 : 1800, this.sfxBus!);
  }

  kill(): void {
    if (!this.ok('kill', 0.04)) return;
    this.sweep(420, 140, this.ctx!.currentTime, 0.09, 'square', 0.025, this.sfxBus!);
  }

  gem(combo: number): void {
    if (!this.ok('gem', 0.03)) return;
    const f = 900 * Math.pow(2, (Math.min(combo, 24) % 12) / 12);
    this.tone(f, this.ctx!.currentTime, 0.08, 'sine', 0.04, this.sfxBus!, 8000);
  }

  levelUp(): void {
    if (!this.ok('level', 0.2)) return;
    const t = this.ctx!.currentTime;
    [0, 4, 7, 12].forEach((s, i) => this.tone(523 * Math.pow(2, s / 12), t + i * 0.07, 0.35, 'triangle', 0.08, this.sfxBus!, 9000));
  }

  chest(): void {
    if (!this.ok('chest', 0.3)) return;
    const t = this.ctx!.currentTime;
    [0, 7, 12, 16, 19, 24].forEach((s, i) => this.tone(392 * Math.pow(2, s / 12), t + i * 0.09, 0.5, 'sine', 0.07, this.sfxBus!, 9000));
  }

  pickup(): void {
    if (!this.ok('pickup', 0.08)) return;
    const t = this.ctx!.currentTime;
    this.tone(660, t, 0.1, 'square', 0.04, this.sfxBus!, 5000);
    this.tone(990, t + 0.08, 0.14, 'square', 0.04, this.sfxBus!, 5000);
  }

  hurt(): void {
    if (!this.ok('hurt', 0.12)) return;
    const t = this.ctx!.currentTime;
    this.sweep(260, 90, t, 0.22, 'sawtooth', 0.09, this.sfxBus!);
  }

  dash(): void {
    if (!this.ok('dash', 0.1)) return;
    this.noiseBurst(this.ctx!.currentTime, 0.22, 0.07, 2200, this.sfxBus!);
  }

  explode(size: number): void {
    if (!this.ok('explode', 0.05)) return;
    const t = this.ctx!.currentTime;
    this.noiseBurst(t, 0.3 + size * 0.5, 0.08 + size * 0.12, 600 + size * 400, this.sfxBus!);
    this.sweep(120, 40, t, 0.3 + size * 0.3, 'sine', 0.1 + size * 0.1, this.sfxBus!);
  }

  thunder(): void {
    if (!this.ok('thunder', 0.2)) return;
    const t = this.ctx!.currentTime;
    this.noiseBurst(t, 0.9, 0.16, 900, this.sfxBus!);
  }

  wind(): void {
    if (!this.ok('wind', 1)) return;
    this.noiseBurst(this.ctx!.currentTime, 5.5, 0.05, 500, this.sfxBus!, 1.5);
  }

  roar(size: number): void {
    if (!this.ok('roar', 0.5)) return;
    const t = this.ctx!.currentTime;
    this.sweep(110, 55, t, 1.2, 'sawtooth', 0.08 * size + 0.04, this.sfxBus!);
    this.noiseBurst(t, 1.0, 0.06 * size, 400, this.sfxBus!);
  }

  ui(): void {
    if (!this.ok('ui', 0.05)) return;
    this.tone(740, this.ctx!.currentTime, 0.06, 'triangle', 0.05, this.sfxBus!, 6000);
  }

  victory(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [0, 4, 7, 12, 16, 19, 24].forEach((s, i) => this.tone(392 * Math.pow(2, s / 12), t + i * 0.12, 0.9, 'triangle', 0.08, this.sfxBus!, 9000));
  }

  defeat(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    [12, 8, 5, 0].forEach((s, i) => this.tone(220 * Math.pow(2, s / 12), t + i * 0.25, 0.8, 'sawtooth', 0.05, this.sfxBus!, 1200));
  }

  dispose(): void {
    window.removeEventListener('pointerdown', this.unlock);
    window.removeEventListener('keydown', this.unlock);
    void this.ctx?.close();
    this.ctx = null;
  }

  // --- primitives ---

  private tone(freq: number, t: number, dur: number, type: OscillatorType, vol: number, bus: AudioNode, cutoff: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + Math.min(0.03, dur * 0.3));
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(filter).connect(gain).connect(bus);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private sweep(f0: number, f1: number, t: number, dur: number, type: OscillatorType, vol: number, bus: AudioNode): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain).connect(bus);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private noiseBurst(t: number, dur: number, vol: number, cutoff: number, bus: AudioNode, attack = 0.005): void {
    const ctx = this.ctx!;
    if (!this.noise) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(gain).connect(bus);
    src.start(t);
    src.stop(t + dur + 0.05);
  }

  private thump(t: number, vol: number, bus: AudioNode): void {
    this.sweep(140, 45, t, 0.18, 'sine', vol, bus);
  }

  private hat(t: number, vol: number, bus: AudioNode): void {
    this.noiseBurst(t, 0.04, vol, 9000, bus);
  }
}
